import React, { useState } from "react";
import { InteractiveElementSchema } from "./LessonTemplateRenderer";
import { ColorWheelExercises } from "./ColorWheelExercises";
import { ColorMixingSimulator } from "./ColorMixingSimulator";
import { ColorSpaceComparison } from "./ColorSpaceComparison";
import { QuizEngine, QuizConfig } from "./QuizEngine";
import { getQuizConfig } from "../../data/education/quizContent";

// Standardized interface for all interactive components in lessons
interface InteractiveComponentProps {
  element: InteractiveElementSchema;
  onComplete: (result: any) => void;
  isCompleted?: boolean;
}

// Adapter wrapper for ColorWheelExercises
const ColorWheelAdapter: React.FC<InteractiveComponentProps> = ({
  element,
  onComplete,
  isCompleted,
}) => {
  const handleExerciseComplete = (result: any) => {
    onComplete({
      type: "color-wheel-exercise",
      score: result.score || 100,
      completed: true,
      data: result,
    });
  };

  return (
    <div className="border rounded-lg p-6 bg-muted/50">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">{element.title}</h4>
        {isCompleted && (
          <div className="flex items-center gap-2 text-green-600">
            <span className="text-sm">Completed</span>
            <div className="w-4 h-4 rounded-full bg-green-600"></div>
          </div>
        )}
      </div>
      {element.description && (
        <p className="text-sm text-muted-foreground mb-4">
          {element.description}
        </p>
      )}
      <ColorWheelExercises
        onExerciseComplete={handleExerciseComplete}
        className={isCompleted ? "opacity-75 pointer-events-none" : ""}
      />
    </div>
  );
};

// Adapter wrapper for ColorMixingSimulator
const ColorMixingAdapter: React.FC<InteractiveComponentProps> = ({
  element,
  onComplete,
  isCompleted,
}) => {
  const handleMixComplete = (result: any) => {
    onComplete({
      type: "color-mixing-exercise",
      score: result.score || 100,
      completed: true,
      data: result,
    });
  };

  return (
    <div className="border rounded-lg p-6 bg-muted/50">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">{element.title}</h4>
        {isCompleted && (
          <div className="flex items-center gap-2 text-green-600">
            <span className="text-sm">Completed</span>
            <div className="w-4 h-4 rounded-full bg-green-600"></div>
          </div>
        )}
      </div>
      {element.description && (
        <p className="text-sm text-muted-foreground mb-4">
          {element.description}
        </p>
      )}
      <ColorMixingSimulator
        initialMode="education"
        onMixComplete={handleMixComplete}
        className={isCompleted ? "opacity-75 pointer-events-none" : ""}
      />
    </div>
  );
};

// Adapter wrapper for ColorSpaceComparison
const ColorSpaceAdapter: React.FC<InteractiveComponentProps> = ({
  element,
  onComplete,
  isCompleted,
}) => {
  const handleComparisonComplete = (result: any) => {
    onComplete({
      type: "color-space-exercise",
      score: result.score || 100,
      completed: true,
      data: result,
    });
  };

  return (
    <div className="border rounded-lg p-6 bg-muted/50">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">{element.title}</h4>
        {isCompleted && (
          <div className="flex items-center gap-2 text-green-600">
            <span className="text-sm">Completed</span>
            <div className="w-4 h-4 rounded-full bg-green-600"></div>
          </div>
        )}
      </div>
      {element.description && (
        <p className="text-sm text-muted-foreground mb-4">
          {element.description}
        </p>
      )}
      <ColorSpaceComparison
        onComparisonComplete={handleComparisonComplete}
        className={isCompleted ? "opacity-75 pointer-events-none" : ""}
      />
    </div>
  );
};

// Quiz component using new QuizEngine
const QuizAdapter: React.FC<InteractiveComponentProps> = ({
  element,
  onComplete,
  isCompleted,
}) => {
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);

  // Get quiz config from content library or use element props
  const quizConfig: QuizConfig = element.props?.quizId
    ? getQuizConfig(element.props.quizId) || {
        id: element.id,
        title: element.title,
        description: element.description || "Interactive quiz",
        questions: [],
        timeLimit: 15,
        passingScore: 80,
        allowRetries: true,
        showCorrectAnswers: true,
        randomizeQuestions: false,
        randomizeOptions: true,
        difficulty: "beginner",
        category: "color-theory",
      }
    : {
        id: element.id,
        title: element.title,
        description: element.description || "Interactive quiz",
        questions: element.props?.questions || [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What are primary colors in additive color mixing?",
            options: [
              "Red, Yellow, Blue",
              "Red, Green, Blue",
              "Cyan, Magenta, Yellow",
            ],
            correctAnswer: "Red, Green, Blue",
            explanation:
              "In additive color mixing (used in digital displays), primary colors are Red, Green, and Blue (RGB).",
            points: 10,
            difficulty: "beginner",
          },
        ],
        timeLimit: element.props?.timeLimit || 15,
        passingScore: 80,
        allowRetries: true,
        showCorrectAnswers: true,
        randomizeQuestions: false,
        randomizeOptions: true,
        difficulty: "beginner",
        category: "color-theory",
      };

  const handleQuizComplete = (
    results: any[],
    score: number,
    passed: boolean,
  ) => {
    setQuizCompleted(true);
    setShowQuiz(false);
    onComplete({
      type: "quiz",
      score,
      completed: passed,
      data: { results, score, passed },
    });
  };

  if (showQuiz) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <QuizEngine config={quizConfig} onComplete={handleQuizComplete} />
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-6 bg-muted/50">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">{element.title}</h4>
        {(isCompleted || quizCompleted) && (
          <div className="flex items-center gap-2 text-green-600">
            <span className="text-sm">Completed</span>
            <div className="w-4 h-4 rounded-full bg-green-600"></div>
          </div>
        )}
      </div>
      {element.description && (
        <p className="text-sm text-muted-foreground mb-4">
          {element.description}
        </p>
      )}
      <div className="space-y-4">
        <div className="bg-white rounded-lg p-6 border">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-blue-400 to-purple-400 flex items-center justify-center">
              <span className="text-2xl">📝</span>
            </div>
            <div>
              <h5 className="font-medium mb-2">Interactive Quiz</h5>
              <p className="text-sm text-gray-600">
                Test your knowledge with questions about this topic.
              </p>
              {quizConfig.questions && (
                <p className="text-xs text-gray-500 mt-1">
                  {quizConfig.questions.length} questions •{" "}
                  {quizConfig.timeLimit} minutes • {quizConfig.passingScore}% to
                  pass
                </p>
              )}
            </div>
          </div>
        </div>
        {!(isCompleted || quizCompleted) && (
          <button
            onClick={() => setShowQuiz(true)}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Start Quiz
          </button>
        )}
        {(isCompleted || quizCompleted) && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-medium">Quiz completed!</p>
            <p className="text-green-700 text-sm mt-1">
              You've successfully completed this quiz.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Generic exercise component for other interactive elements
const GenericExerciseAdapter: React.FC<InteractiveComponentProps> = ({
  element,
  onComplete,
  isCompleted,
}) => {
  const handleComplete = () => {
    onComplete({
      type: element.type,
      score: 100,
      completed: true,
      data: { elementId: element.id },
    });
  };

  return (
    <div className="border rounded-lg p-6 bg-muted/50">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">{element.title}</h4>
        {isCompleted && (
          <div className="flex items-center gap-2 text-green-600">
            <span className="text-sm">Completed</span>
            <div className="w-4 h-4 rounded-full bg-green-600"></div>
          </div>
        )}
      </div>
      {element.description && (
        <p className="text-sm text-muted-foreground mb-4">
          {element.description}
        </p>
      )}
      <div className="bg-white rounded-lg p-8 border-2 border-dashed border-gray-300">
        <div className="text-center text-muted-foreground">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-400 to-purple-400"></div>
          <p className="font-medium">Interactive Exercise: {element.type}</p>
          <p className="text-sm mt-2">
            This interactive component will be implemented here.
          </p>
          {element.required && (
            <p className="text-xs text-orange-600 mt-2">
              Required for completion
            </p>
          )}
        </div>
      </div>
      {!isCompleted && element.required && (
        <div className="mt-4">
          <button
            onClick={handleComplete}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Mark as Complete
          </button>
        </div>
      )}
    </div>
  );
};

// Component registry mapping element types to React components
export const interactiveElementRegistry: Record<
  string,
  React.FC<InteractiveComponentProps>
> = {
  "color-wheel": ColorWheelAdapter,
  "harmony-explorer": ColorWheelAdapter, // Reuse color wheel for harmony exercises
  "color-mixer": ColorMixingAdapter,
  quiz: QuizAdapter,
  exercise: GenericExerciseAdapter,
  "interactive-demo": GenericExerciseAdapter,
  "color-space-comparison": ColorSpaceAdapter,
};

// Function to get component for element type
export const getInteractiveComponent = (
  elementType: string,
): React.FC<InteractiveComponentProps> => {
  return interactiveElementRegistry[elementType] || GenericExerciseAdapter;
};

// Function to check if element type is supported
export const isSupportedElementType = (elementType: string): boolean => {
  return elementType in interactiveElementRegistry;
};

// List of all supported element types
export const supportedElementTypes = Object.keys(interactiveElementRegistry);
